import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { PageHero, Section } from "@/components/Section";
import { postMessage } from "@/lib/api";
import { BRAND, IMAGES } from "@/lib/site";

const SUJETS = {
  contact: "Demande d'information",
  inscription: "Demande de dossier d'inscription",
  visite: "Demande de visite",
};

export default function Contact() {
  const [params] = useSearchParams();
  const initialType = params.get("type") === "inscription" ? "inscription" : "contact";
  const [form, setForm] = useState({
    nom: "",
    email: "",
    telephone: "",
    sujet: SUJETS[initialType] || SUJETS.contact,
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await postMessage({ ...form, type: initialType === "inscription" ? "inscription" : "contact" });
      toast.success("Votre message a bien été envoyé. Nous vous recontactons rapidement.");
      setForm({ nom: "", email: "", telephone: "", sujet: SUJETS.contact, message: "" });
    } catch {
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Contact"
        title="Une question ? Nous sommes à votre écoute"
        subtitle="Pour une demande d'information, une visite ou un dossier d'inscription, n'hésitez pas à nous écrire ou à venir nous rencontrer."
        image={IMAGES.campus}
      />

      <Section>
        <div className="grid gap-12 lg:grid-cols-[1fr_1.2fr]">
          <div className="space-y-6">
            <InfoBlock icon={MapPin} title="Adresse">
              {BRAND.adresse}
            </InfoBlock>
            <InfoBlock icon={Phone} title="Téléphone">
              <a href={`tel:${BRAND.telephone.replace(/\s/g, "")}`} className="hover:text-brand-sand-dark">
                {BRAND.telephone}
              </a>
            </InfoBlock>
            <InfoBlock icon={Mail} title="Email">
              <a href={`mailto:${BRAND.email}`} className="hover:text-brand-sand-dark">
                {BRAND.email}
              </a>
            </InfoBlock>
            <InfoBlock icon={Clock} title="Horaires d'accueil">
              Lundi → vendredi : 8h00 – 17h30
              <br />
              Samedi : sur rendez-vous
            </InfoBlock>
          </div>

          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="rounded-3xl border border-black/5 bg-brand-cream p-8 shadow-sm"
          >
            <div className="grid gap-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Nom complet" name="nom" value={form.nom} onChange={onChange} required testid="contact-nom" />
                <Field label="Téléphone" name="telephone" value={form.telephone} onChange={onChange} testid="contact-telephone" />
              </div>
              <Field label="Email" type="email" name="email" value={form.email} onChange={onChange} required testid="contact-email" />
              <Field label="Sujet" name="sujet" value={form.sujet} onChange={onChange} required testid="contact-sujet" />
              <div>
                <label className="text-sm font-medium text-brand-slate-dark">Votre message</label>
                <textarea
                  required
                  rows={5}
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  data-testid="contact-message"
                  className="mt-2 w-full rounded-2xl border border-border bg-brand-cream px-4 py-3 text-sm outline-none transition-all focus:border-brand-slate focus:ring-2 focus:ring-brand-sage/40"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="mt-2 inline-flex items-center justify-center rounded-full bg-brand-slate px-6 py-3.5 text-sm font-semibold text-brand-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-slate-dark disabled:opacity-60"
              >
                {submitting ? "Envoi en cours…" : "Envoyer mon message"}
              </button>
            </div>
          </form>
        </div>
      </Section>

      <Section wrapper="bg-brand-sand-wrapper/40" eyebrow="Plan d'accès" title="Nous rendre visite">
        <div className="overflow-hidden rounded-3xl border border-black/5 shadow-sm">
          <iframe
            title="Plan d'accès"
            data-testid="map-iframe"
            src="https://www.google.com/maps?q=Angers%2C+France&output=embed"
            width="100%"
            height="450"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </Section>
    </div>
  );
}

const InfoBlock = ({ icon: Icon, title, children }) => (
  <div className="flex items-start gap-4 rounded-2xl border border-black/5 bg-brand-cream p-6">
    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-brand-sand/30 text-brand-slate-dark">
      <Icon className="h-5 w-5" />
    </span>
    <div>
      <p className="font-heading text-base font-semibold text-brand-slate-dark">{title}</p>
      <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{children}</p>
    </div>
  </div>
);

const Field = ({ label, name, value, onChange, type = "text", required = false, testid }) => (
  <div>
    <label className="text-sm font-medium text-brand-slate-dark">
      {label} {required && <span className="text-brand-sand-dark">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={testid}
      className="mt-2 w-full rounded-2xl border border-border bg-brand-cream px-4 py-3 text-sm outline-none transition-all focus:border-brand-slate focus:ring-2 focus:ring-brand-sage/40"
    />
  </div>
);
